import React, { useEffect, useMemo, useState } from 'react';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  RobotOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { Layout, Menu, Button, theme, Flex, Space, Tooltip } from 'antd';
import { Outlet, useLocation } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase-config';
import { useAppDispatch, useAppSelector, useAuth } from '../store/hooks';
import { clearUser } from '../store/usersSlice';
import { AppRoutes } from '../routes';
import { Footer } from 'antd/es/layout/layout';
import CompanySelect from '../components/Company/CompanySelect';
import UserSettingsModal from '../components/User/UserSettingsModal';
import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import { ToolOutlined } from '@ant-design/icons';
import './RootLayout.css';

const { Header, Sider, Content } = Layout;

const RootLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const selectedCompany = useAppSelector((state) => state.companies.selectedCompany)
  const companies = useAppSelector((state) => state.companies.companies)
  const {
    token: { borderRadiusLG, boxShadowSecondary, colorBgContainer, colorBgLayout, colorBorderSecondary },
  } = theme.useToken();
  const claims = useAuth().claims
  const isDenseWorkspace = location.pathname === AppRoutes.customProductGenerator;

  useEffect(() => {
    document.documentElement.style.setProperty('--app-sidebar-width', collapsed ? '76px' : '240px');
  }, [collapsed]);

  const handleLogout = async () => {
    try {
      await signOut(auth)
      dispatch(clearUser())
    } catch (error) {
      console.error(error)
    }
  }

  const menuItems = useMemo(() => {
    const companyAccess = claims?.companyAccess || {};
    const hasCompanyAccess = claims?.superadmin
      || (companyAccess['*'] ?? 0) >= 10
      || Object.values(companyAccess).some((lvl: any) => (typeof lvl === 'number' ? lvl : 0) >= 10)
      || companies.length > 0
    const items: ItemType<MenuItemType>[] = []
    // {
    //   key: '1',
    //   icon: <HomeOutlined />,
    //   label: 'Home',
    //   onClick: () => navigate(AppRoutes.home)
    // },
    const customEnabled = selectedCompany?.modules?.customContent === true;
    const canSeeCustomProducts = customEnabled && (claims?.superadmin || hasCompanyAccess);

    if (canSeeCustomProducts) {
      items.push({
        key: AppRoutes.customProductGenerator,
        icon: <RobotOutlined />,
        label: 'Product Generator',
        onClick: () => navigate(AppRoutes.customProductGenerator)
      })
    }

    const brandResearchEnabled = selectedCompany?.modules?.brandResearch === true;
    const canSeeBrandResearch = brandResearchEnabled && (claims?.superadmin || hasCompanyAccess);

    if (canSeeBrandResearch) {
      items.push({
        key: AppRoutes.brandResearch,
        icon: <TagsOutlined />,
        label: 'Brand Research',
        onClick: () => navigate(AppRoutes.brandResearch)
      })
    }

    const shopifyEnabled = selectedCompany?.modules?.shopify === true;
    const canSeeShopify = shopifyEnabled
      && (claims?.superadmin || claims?.features?.featureShopify || hasCompanyAccess);

    if (canSeeShopify) {
      items.push(
        {
          key: '2',
          icon: <ShoppingCartOutlined />,
          label: 'Shopify',
          onClick: () => navigate(AppRoutes.shopify)
        },
      )
    }

    if (claims?.superadmin) {
      items.push({
        key: AppRoutes.adminCenter,
        icon: <ToolOutlined />,
        label: 'Admin',
        onClick: () => navigate(AppRoutes.adminCenter)
      })
    }

    return items
  }, [claims, navigate, selectedCompany, companies])

  useEffect(() => {
    const companyAccess = claims?.companyAccess || {};
    const hasCompanyAccess = claims?.superadmin
      || (companyAccess['*'] ?? 0) >= 10
      || Object.values(companyAccess).some((lvl: any) => (typeof lvl === 'number' ? lvl : 0) >= 10)
      || companies.length > 0;

    const canSeeCustomProducts = selectedCompany?.modules?.customContent === true
      && (claims?.superadmin || hasCompanyAccess);
    const canSeeBrandResearch = selectedCompany?.modules?.brandResearch === true
      && (claims?.superadmin || hasCompanyAccess);
    const canSeeShopify = selectedCompany?.modules?.shopify === true
      && (claims?.superadmin || claims?.features?.featureShopify || hasCompanyAccess);

    const onCustomProducts = location.pathname === AppRoutes.customProductGenerator;
    const onBrandResearch = location.pathname === AppRoutes.brandResearch;
    const onShopify = location.pathname === AppRoutes.shopify;

    if (onCustomProducts && !canSeeCustomProducts) {
      if (canSeeBrandResearch) {
        navigate(AppRoutes.brandResearch, { replace: true });
      } else if (canSeeShopify) {
        navigate(AppRoutes.shopify, { replace: true });
      } else if (claims?.superadmin) {
        navigate(AppRoutes.adminCenter, { replace: true });
      } else {
        navigate(AppRoutes.home, { replace: true });
      }
    }

    if (onBrandResearch && !canSeeBrandResearch) {
      if (canSeeCustomProducts) {
        navigate(AppRoutes.customProductGenerator, { replace: true });
      } else if (canSeeShopify) {
        navigate(AppRoutes.shopify, { replace: true });
      } else if (claims?.superadmin) {
        navigate(AppRoutes.adminCenter, { replace: true });
      } else {
        navigate(AppRoutes.home, { replace: true });
      }
    }

    if (onShopify && !canSeeShopify) {
      if (canSeeCustomProducts) {
        navigate(AppRoutes.customProductGenerator, { replace: true });
      } else if (canSeeBrandResearch) {
        navigate(AppRoutes.brandResearch, { replace: true });
      } else if (claims?.superadmin) {
        navigate(AppRoutes.adminCenter, { replace: true });
      } else {
        navigate(AppRoutes.home, { replace: true });
      }
    }
  }, [claims, companies, location.pathname, navigate, selectedCompany]);

  return (
    <Layout style={{ height: '100vh', background: colorBgLayout }}>
      <Sider
        className="root-layout-sidebar"
        theme="light"
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        collapsedWidth={76}
      >
        {/* TODO: add logo here
        */}
        {/* <div className="demo-logo-vertical" /> */}
        <div className={collapsed ? 'root-layout-logo root-layout-logo--collapsed' : 'root-layout-logo'}>
          <img
            src={collapsed ? '/logo512.png' : '/logo_full_orange.png'}
            alt="Seophenix Logo"
          />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
        />
      </Sider>
      <Layout style={{ background: colorBgLayout, minHeight: 0 }}>
        <Header style={{ padding: 0, background: colorBgContainer, borderBottom: `1px solid ${colorBorderSecondary}` }}>
          <Flex justify="space-between" align='center'>

            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
            <Space size="small" align='center'>
              <CompanySelect />
              <UserSettingsModal />
              <Tooltip title="Logout">
                <Button
                  onClick={handleLogout}
                  danger
                  icon={<LogoutOutlined />}
                  style={{
                    marginRight: 16,
                    padding: 0,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              </Tooltip>
            </Space>

          </Flex>
        </Header>
        <Content
          style={{
            margin: "24px 16px",
            padding: isDenseWorkspace ? 0 : 24,
            flex: isDenseWorkspace ? "1 1 auto" : undefined,
            minHeight: 0,
            height: isDenseWorkspace ? "auto" : "calc(100vh - 64px - 24px)", // Adjust height for scrollable area
            overflowY: isDenseWorkspace ? "hidden" : "auto", // Enable vertical scrolling
            background: colorBgContainer,
            border: `1px solid ${colorBorderSecondary}`,
            borderRadius: borderRadiusLG,
            boxShadow: boxShadowSecondary,
          }}
        >
          <Outlet />
        </Content>
        <Footer style={{ textAlign: 'center', background: colorBgLayout }}>
          <Space>
            <Link to={AppRoutes.imprint} style={{ color: 'inherit', textDecoration: 'none' }}>
              Imprint
            </Link>
          </Space>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default RootLayout;
